import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, Dumbbell, ChevronRight } from 'lucide-react';
import type { Member } from '../state/types';
import { AvatarImage } from './AvatarImage';
export function Logo({
  compact = false,
  variant = 'horizontal',
}: {
  compact?: boolean;
  variant?: 'horizontal' | 'stacked';
}) {
  return (
    <span
      className={`brand${compact ? ' brand-compact' : variant === 'stacked' ? ' brand-stacked' : ''}`}
      role="img"
      aria-label="GarageGymBros"
    >
      {!compact && variant === 'stacked' ? (
        <img className="brand-logo" src="/branding/ggb-logo.svg" alt="" aria-hidden="true" />
      ) : (
        <>
          <span className="brand-mark" aria-hidden="true" />
          {!compact && (
            <img
              className="brand-wordmark"
              src="/branding/ggb-wordmark.svg"
              alt=""
              aria-hidden="true"
            />
          )}
        </>
      )}
    </span>
  );
}
export function Avatar({
  member,
  size = 40,
  probe = false,
}: {
  member?: Partial<Member>;
  size?: number;
  /** Staff directories may probe delivery before avatar_media_id is known. */
  probe?: boolean;
}) {
  return (
    <span
      className="member-avatar"
      style={{
        width: size,
        height: size,
        background: member?.avatar_color || '#555555',
        fontSize: size * 0.32,
      }}
      aria-label={member?.display_name}
    >
      <AvatarImage
        userId={member?.id}
        source={member?.avatar_url}
        mediaId={member?.avatar_media_id}
        probe={probe}
        size={size}
        fallback={
          member?.initials ||
          member?.display_name
            ?.split(' ')
            .map((w) => w[0])
            .slice(0, 2)
            .join('') ||
          'G'
        }
      />
    </span>
  );
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
  drawer = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  drawer?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const box = ref.current;
    box?.querySelector<HTMLElement>('input,button,select,textarea,a')?.focus();
    function key(e: KeyboardEvent) {
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'Tab' && box) {
        const all = Array.from(
          box.querySelectorAll<HTMLElement>(
            'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
          ),
        ).filter((el) => el.offsetParent !== null);
        if (!all.length) {
          e.preventDefault();
          return;
        }
        const first = all[0],
          last = all[all.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    }
    document.addEventListener('keydown', key);
    const old = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', key);
      document.body.style.overflow = old;
      prev?.focus();
    };
  }, []);
  // Escape parent stacking contexts (hero overflow/transforms, sticky columns)
  // so the overlay always sits above page chrome like feed tabs and asides.
  return createPortal(
    <div
      className={`modal-backdrop${drawer ? ' modal-backdrop-drawer' : ''}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`modal${wide ? ' modal-wide' : ''}${drawer ? ' modal-drawer' : ''}`}
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <div className="modal-heading">
          <h2 id="modal-title">{title}</h2>
          <button
            className="icon-button"
            aria-label={drawer ? 'Close panel' : 'Close dialog'}
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <Dumbbell size={30} />
      </div>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}
export function PageHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="heading-action">{action}</div>}
    </div>
  );
}
export function ExternalLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="external-link">
      {children}
      <ChevronRight size={14} />
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  );
}
export function EquipmentArt({ kind = 'rack', large = false }: { kind?: string; large?: boolean }) {
  return (
    <svg
      className={`equipment-art ${large ? 'large' : ''}`}
      viewBox="0 0 320 240"
      role="img"
      aria-label={`Illustration of ${kind.replaceAll('-', ' ')}`}
    >
      <defs>
        <linearGradient id={`metal-${kind}`} x2="1" y2="1">
          <stop stopColor="#686868" />
          <stop offset=".5" stopColor="#393939" />
          <stop offset="1" stopColor="#1a1a1a" />
        </linearGradient>
        <radialGradient id={`glow-${kind}`}>
          <stop stopColor="#707070" stopOpacity=".2" />
          <stop offset="1" stopColor="#1d1d1d" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="160" cy="205" rx="113" ry="15" fill="#090909" opacity=".6" />
      <circle cx="160" cy="115" r="125" fill={`url(#glow-${kind})`} />
      <g fill={`url(#metal-${kind})`} stroke="#808080" strokeWidth="1.5" strokeLinejoin="round">
        {['rack', 'cable'].includes(kind) ? (
          <>
            <path d="M78 201V45l12-7 5 161M228 201V45l-12-7-5 161M90 38h126v10H90M84 190l-20 14v7h51v-8l-20-8M216 190l-15 13v8h53v-7l-26-14" />
            <path
              d="M84 67h139M84 102h139M84 137h139M84 172h139"
              stroke="#171717"
              strokeWidth="3"
            />
            {[65, 85, 105, 125, 145, 165, 185].map((y) => (
              <g key={y}>
                <circle cx="88" cy={y} r="2" fill="#111111" />
                <circle cx="220" cy={y} r="2" fill="#111111" />
              </g>
            ))}
            <path d="M56 122h205" stroke="#a8a8a8" strokeWidth="5" />
            <path d="M63 103v38M70 96v52M248 96v52M255 103v38" stroke="#101010" strokeWidth="9" />
            {kind === 'cable' && (
              <>
                <path d="M106 60v117h22V60M183 60v117h22V60" />
                <path d="M111 53l35 65M200 53l-36 65" stroke="#999999" />
                <path d="M140 117h32" stroke="#ff2a2a" strokeWidth="7" />
              </>
            )}
          </>
        ) : kind === 'bench' ? (
          <>
            <path d="M105 163l-32 40h46l24-32M185 151l32 50h35l-37-55" strokeWidth="7" />
            <path d="M78 142l106-60 17 18-112 64Z" fill="#191919" />
            <path d="M88 165l96-55 58 37-40 27Z" fill="#111111" />
            <path d="M182 110v-16" stroke="#ff2a2a" strokeWidth="7" />
          </>
        ) : kind === 'barbell' ? (
          <>
            <path d="M45 133L272 93" stroke="#c8c8c8" strokeWidth="7" />
            <path
              d="M66 115l6 34M78 105l10 53M91 100l10 58M237 82l10 53M250 83l10 46"
              stroke="#151515"
              strokeWidth="13"
            />
            <path d="M95 111l9 42M239 90l8 39" stroke="#6a6a6a" strokeWidth="3" />
          </>
        ) : ['plates', 'storage'].includes(kind) ? (
          <>
            <path d="M159 60v134M116 202h87M159 148l-47 54M159 148l47 54" strokeWidth="9" />
            {[0, 1, 2].map((a) => (
              <g key={a}>
                <ellipse
                  cx={125 + a * 27}
                  cy={114 + a * 17}
                  rx="40"
                  ry="49"
                  transform={`rotate(-17 ${125 + a * 27} ${114 + a * 17})`}
                  fill={a === 1 ? '#a4373b' : '#222222'}
                />
                <ellipse cx={125 + a * 27} cy={114 + a * 17} rx="8" ry="10" fill="#111111" />
              </g>
            ))}
          </>
        ) : kind === 'kettlebell' ? (
          <>
            <path
              d="M125 115v-20a36 36 0 0 1 72 0v20"
              fill="none"
              stroke="#707070"
              strokeWidth="15"
            />
            <path d="M126 108q-35 24-24 68 7 35 59 34t59-35q10-39-26-68Z" />
            <path d="M139 155h42" stroke="#ff2a2a" strokeWidth="3" />
          </>
        ) : (
          <>
            <g transform="rotate(-20 160 130)">
              <path d="M90 116h149" stroke="#aaaaaa" strokeWidth="9" />
              <path d="M88 91v62M101 82v79M217 82v79M230 91v62" stroke="#181818" strokeWidth="20" />
              <path d="M100 89v65M218 89v65" stroke="#777777" strokeWidth="3" />
            </g>
            <g transform="translate(-12 52) rotate(12 160 130)">
              <path d="M90 116h149" stroke="#aaaaaa" strokeWidth="9" />
              <path d="M88 91v62M101 82v79M217 82v79M230 91v62" stroke="#181818" strokeWidth="20" />
            </g>
          </>
        )}
      </g>
    </svg>
  );
}
